import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import axiosInstance, { weburl } from '../../axiosInstance';
import getDecryptData from '../../helpers/getDecryptData';
import LoadingTable from '../components/LoadingTable';

const statusConfig = {
  0: { label: 'Cancelled', color: '#ef4444', bg: '#fef2f2', icon: 'ri-close-circle-line' },
  1: { label: 'Placed', color: '#3b82f6', bg: '#eff6ff', icon: 'ri-timer-line' },
  2: { label: 'Accepted', color: '#8b5cf6', bg: '#f5f3ff', icon: 'ri-checkbox-circle-line' },
  5: { label: 'Started', color: '#f59e0b', bg: '#fffbeb', icon: 'ri-play-circle-line' },
  7: { label: 'Completed', color: '#10b981', bg: '#ecfdf5', icon: 'ri-checkbox-circle-fill' },
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

const getDateValue = (order) => {
  const date =
    order?.createdAt ||
    order?.updatedAt ||
    order?.endTime ||
    order?.startTime ||
    order?.items?.[0]?.date;
  const parsed = date ? new Date(date) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const isCompleted = (order) => Number(order?.status) === 7;
const isCancelled = (order) => Number(order?.status) === 0;
const isPending = (order) => !isCompleted(order) && !isCancelled(order);

const getDriverImage = (driver) => {
  const image = driver?.profile || driver?.ProfileFile || driver?.profileImage || driver?.avatar || '';
  if (!image || typeof image !== 'string') return '';
  if (image.startsWith('http') || image.startsWith('data:') || image.startsWith('blob:')) return image;
  const cleanImage = image.replace(/\\/g, '/').replace(/^public\//, '');
  const baseUrl = axiosInstance.defaults.baseURL || weburl;
  return `${baseUrl.replace(/\/$/, '')}/${cleanImage.replace(/^\//, '')}`;
};

const mergeProfileFields = (driver, profilesById) => {
  if (!driver || typeof driver !== 'object') return driver;
  const profileUser = profilesById.get(driver._id);
  if (!profileUser) return driver;
  return {
    ...driver,
    profile: driver.profile || profileUser.profile || profileUser.ProfileFile || '',
    ProfileFile: driver.ProfileFile || profileUser.ProfileFile || profileUser.profile || '',
    profileImage: driver.profileImage || profileUser.profileImage || '',
    avatar: driver.avatar || profileUser.avatar || '',
  };
};

const Avatar = ({ user, size = 34 }) => {
  const [imageError, setImageError] = useState(false);
  const image = getDriverImage(user);
  if (image && !imageError) {
    return (
      <img
        className="dash-avatar"
        src={image}
        alt={user?.username || 'User'}
        style={{ width: size, height: size }}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div className="dash-avatar dash-avatar-fallback" style={{ width: size, height: size }}>
      {(user?.username || 'U')[0].toUpperCase()}
    </div>
  );
};

const MiniSparkline = ({ data, color }) => {
  const values = data.length ? data : [0];
  const max = Math.max(...values, 1);
  const points = values
    .map((value, index) => {
      const x = values.length === 1 ? 0 : (index / (values.length - 1)) * 100;
      const y = 34 - (value / max) * 28;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg viewBox="0 0 100 38" preserveAspectRatio="none" className="sparkline">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const LineChart = ({ weekly }) => {
  const max = Math.max(...weekly.flatMap((day) => [day.completed, day.pending, day.cancelled]), 1);
  const makePoints = (key) =>
    weekly
      .map((day, index) => {
        const x = weekly.length === 1 ? 0 : (index / (weekly.length - 1)) * 100;
        const y = 100 - (day[key] / max) * 88;
        return `${x},${y}`;
      })
      .join(' ');

  return (
    <div className="chart-wrap">
      <div className="chart-legend">
        <span><i style={{ background: '#10b981' }} />Completed</span>
        <span><i style={{ background: '#f59e0b' }} />Pending</span>
        <span><i style={{ background: '#ef4444' }} />Cancelled</span>
      </div>
      <svg viewBox="0 0 100 112" preserveAspectRatio="none" className="line-chart">
        {[20, 40, 60, 80, 100].map((y) => (
          <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="#eef2f7" strokeWidth="0.6" />
        ))}
        <polyline points={makePoints('completed')} fill="none" stroke="#10b981" strokeWidth="1.8" />
        <polyline points={makePoints('pending')} fill="none" stroke="#f59e0b" strokeWidth="1.8" />
        <polyline points={makePoints('cancelled')} fill="none" stroke="#ef4444" strokeWidth="1.8" />
      </svg>
      <div className="chart-days">
        {weekly.map((day) => <span key={day.label}>{day.label}</span>)}
      </div>
    </div>
  );
};

const DonutChart = ({ completed, pending, cancelled, total }) => {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const values = [
    { value: completed, color: '#10b981' },
    { value: pending, color: '#f59e0b' },
    { value: cancelled, color: '#ef4444' },
  ];
  let offset = 0;

  return (
    <div className="donut-layout">
      <div className="donut-box">
        <svg viewBox="0 0 120 120" className="donut-chart">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#eef2f7" strokeWidth="18" />
          {values.map((item) => {
            const dash = total ? (item.value / total) * circumference : 0;
            const circle = (
              <circle
                key={item.color}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth="18"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform="rotate(-90 60 60)"
              />
            );
            offset += dash;
            return circle;
          })}
        </svg>
        <div className="donut-center">
          <strong>{formatNumber(total)}</strong>
          <span>Total</span>
        </div>
      </div>
      <div className="donut-list">
        {[
          ['Completed', completed, '#10b981'],
          ['Pending', pending, '#f59e0b'],
          ['Cancelled', cancelled, '#ef4444'],
        ].map(([label, value, color]) => (
          <div className="donut-row" key={label}>
            <i style={{ background: color }} />
            <span>{label}</span>
            <strong>{formatNumber(value)} ({total ? Math.round((value / total) * 100) : 0}%)</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, bg, data, trend }) => (
  <div className="stat-card">
    <div className="stat-top">
      <div className="stat-icon" style={{ color, background: bg }}>
        <i className={icon}></i>
      </div>
      <div>
        <span>{title}</span>
        <strong>{formatNumber(value)}</strong>
        <small className={trend >= 0 ? 'trend-up' : 'trend-down'}>
          <i className={trend >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
          {Math.abs(trend)}% vs last week
        </small>
      </div>
    </div>
    <MiniSparkline data={data} color={color} />
  </div>
);

function Dashboard() {
  const [isUserdata, setisUserdata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customerCount, setCustomerCount] = useState(0);

  useEffect(() => {
    const loadUser = async () => {
      const mydata = await getDecryptData();
      setisUserdata(mydata || null);
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (!isUserdata) return;
    if (Number(isUserdata.type) !== 0) {
      setLoading(false);
      return;
    }

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const [ordersRes, customersRes, driversRes, driverProfilesRes] = await Promise.all([
          axiosInstance.get('/admin/all-order?page=1&limit=1000&search='),
          axiosInstance.get('/admin/all-user?page=1&limit=1000&type=2&status='),
          axiosInstance.get('/all-employee?type=1&longitude=&latitude=&empType=2&status=1'),
          axiosInstance.get('/admin/all-user?page=1&limit=1000&type=1&empType=2&status='),
        ]);

        const profilesById = new Map((driverProfilesRes.data.users || []).map((driver) => [driver._id, driver]));
        const orderList = (ordersRes.data.Order || []).map((order) => ({
          ...order,
          agentId: order.agentId ? mergeProfileFields(order.agentId, profilesById) : order.agentId,
        }));
        const customerList = customersRes.data.users || [];
        const driverList = (driversRes.data.users || [])
          .filter((driver) => String(driver.status) !== '0')
          .map((driver) => mergeProfileFields(driver, profilesById));

        setOrders(orderList);
        setDrivers(driverList);
        setCustomerCount(customersRes.data.totalUsers || customersRes.data.total || customerList.length);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [isUserdata]);

  const dashboard = useMemo(() => {
    const today = new Date();
    const dayStart = new Date(today);
    dayStart.setHours(0, 0, 0, 0);

    const weekLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const week = weekLabels.map((label) => ({ label, completed: 0, pending: 0, cancelled: 0 }));
    const sparkBuckets = Array.from({ length: 14 }, () => ({
      total: 0,
      completed: 0,
      pending: 0,
      cancelled: 0,
      customers: 0,
    }));

    const currentStart = new Date(today);
    currentStart.setDate(today.getDate() - 6);
    currentStart.setHours(0, 0, 0, 0);
    const previousStart = new Date(currentStart);
    previousStart.setDate(currentStart.getDate() - 7);

    let currentWeekTotal = 0;
    let previousWeekTotal = 0;

    orders.forEach((order) => {
      const date = getDateValue(order);
      const status = Number(order.status);
      if (date) {
        const dayIndex = (date.getDay() + 6) % 7;
        if (isCompleted(order)) week[dayIndex].completed += 1;
        else if (isCancelled(order)) week[dayIndex].cancelled += 1;
        else week[dayIndex].pending += 1;

        const diffDays = Math.floor((dayStart - new Date(date.getFullYear(), date.getMonth(), date.getDate())) / 86400000);
        if (diffDays >= 0 && diffDays < 14) {
          const bucket = sparkBuckets[13 - diffDays];
          bucket.total += 1;
          if (status === 7) bucket.completed += 1;
          else if (status === 0) bucket.cancelled += 1;
          else bucket.pending += 1;
        }

        if (date >= currentStart) currentWeekTotal += 1;
        else if (date >= previousStart && date < currentStart) previousWeekTotal += 1;
      }
    });

    const completed = orders.filter(isCompleted).length;
    const cancelled = orders.filter(isCancelled).length;
    const pending = orders.filter(isPending).length;
    const total = orders.length;
    const trend = previousWeekTotal ? Math.round(((currentWeekTotal - previousWeekTotal) / previousWeekTotal) * 100) : currentWeekTotal ? 100 : 0;

    const driverMap = new Map();
    orders.forEach((order) => {
      if (!order.agentId) return;
      const id = order.agentId?._id || order.agentId;
      const existing = driverMap.get(id) || { driver: order.agentId, rides: 0, completed: 0 };
      existing.rides += 1;
      if (isCompleted(order)) existing.completed += 1;
      driverMap.set(id, existing);
    });

    const topDrivers = Array.from(driverMap.values())
      .sort((a, b) => b.rides - a.rides)
      .slice(0, 5);

    return {
      total,
      completed,
      pending,
      cancelled,
      trend,
      weekly: week,
      spark: sparkBuckets,
      topDrivers,
      recent: [...orders]
        .sort((a, b) => (getDateValue(b)?.getTime() || 0) - (getDateValue(a)?.getTime() || 0))
        .slice(0, 5),
      liveDrivers: drivers
        .filter((driver) => Number(driver.online) === 1 || driver.onDuty)
        .slice(0, 5),
    };
  }, [orders, drivers]);

  if (isUserdata && Number(isUserdata.type) === 1) {
    return (
      <>
        <Sidebar />
        <main className="content">
          <Navbar />
          <div className="row mt-3">
            <div className="col-xl-4 col-md-6">
              <Link className="simple-dash-card bg-primary" to="/all-transaction">My Wallet</Link>
            </div>
            <div className="col-xl-4 col-md-6">
              <Link className="simple-dash-card bg-warning" to="/all-jobs">All Jobs</Link>
            </div>
            <div className="col-xl-4 col-md-6">
              <Link className="simple-dash-card bg-success" to="/profile/edit">Profile Edit</Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="modern-dashboard">
          {loading ? (
            <LoadingTable />
          ) : (
            <>
              <div className="stats-grid">
                <StatCard title="Total Rides" value={dashboard.total} icon="ri-taxi-line" color="#5b3df5" bg="#f0edff" data={dashboard.spark.map((d) => d.total)} trend={dashboard.trend} />
                <StatCard title="Completed Rides" value={dashboard.completed} icon="ri-clipboard-line" color="#238be6" bg="#eaf4ff" data={dashboard.spark.map((d) => d.completed)} trend={dashboard.trend} />
                <StatCard title="Pending Rides" value={dashboard.pending} icon="ri-time-line" color="#f59e0b" bg="#fff4db" data={dashboard.spark.map((d) => d.pending)} trend={dashboard.trend} />
                <StatCard title="Cancelled Rides" value={dashboard.cancelled} icon="ri-close-circle-line" color="#ef4444" bg="#ffecef" data={dashboard.spark.map((d) => d.cancelled)} trend={dashboard.trend} />
                <StatCard title="Total Customers" value={customerCount} icon="ri-group-line" color="#10b981" bg="#e9fbf4" data={dashboard.spark.map((d, i) => d.total + i)} trend={dashboard.trend} />
              </div>

              <div className="main-grid">
                <section className="dash-panel ride-overview">
                  <div className="panel-head">
                    <h2>Ride Overview</h2>
                    <span>This Week</span>
                  </div>
                  <LineChart weekly={dashboard.weekly} />
                </section>

                <section className="dash-panel status-panel">
                  <div className="panel-head"><h2>Rides by Status</h2></div>
                  <DonutChart completed={dashboard.completed} pending={dashboard.pending} cancelled={dashboard.cancelled} total={dashboard.total} />
                </section>

                <section className="dash-panel w-100">
                  <div className="panel-head">
                    <h2>Live Drivers</h2>
                    <Link to="/live-users">View All</Link>
                  </div>
                  <div className="list-stack">
                    {dashboard.liveDrivers.length === 0 ? <p className="empty-text">No live drivers</p> : dashboard.liveDrivers.map((driver) => (
                      <Link className="driver-row" to={`/user/${driver._id}`} key={driver._id}>
                        <Avatar user={driver} size={32} />
                        <strong title={driver.username}>{driver.username}</strong>
                        <span><i />{driver.onDuty ? 'On Ride' : 'Online'}</span>
                        <em>{driver.totalRides || 0} Rides</em>
                        <i className="ri-arrow-right-s-line"></i>
                      </Link>
                    ))}
                  </div>
                </section>
              </div>

           <div className="d-flex flex-column flex-md-row gap-4">

                <section className="dash-panel w-100">
                  <div className="panel-head">
                    <h2>Top Performing Drivers</h2>
                    <Link to="/reports/drivers">View All</Link>
                  </div>
                  <div className="rank-list">
                    {dashboard.topDrivers.length === 0 ? <p className="empty-text">No driver data</p> : dashboard.topDrivers.map((item, index) => (
                      <div className="rank-row" key={item.driver?._id || index}>
                        <span>{index + 1}</span>
                        <Avatar user={item.driver} size={32} />
                        <strong title={item.driver?.username || 'Driver'}>{item.driver?.username || 'Driver'}</strong>
                        <em>{item.rides} Rides</em>
                        <small>{item.completed} completed</small>
                      </div>
                    ))}
                  </div>
                </section>

             

                <section className="dash-panel w-100">
                  <div className="panel-head"><h2>Quick Actions</h2></div>
                  <div className="quick-grid">
                    <Link to="/create-order"><i className="ri-taxi-line" style={{ color: '#5b3df5' }} />Create New Ride</Link>
                    <Link to="/create-user"><i className="ri-user-add-line" style={{ color: '#238be6' }} />Add Customer / Staff</Link>
                    <Link to="/all-order"><i className="ri-list-check-2" style={{ color: '#f59e0b' }} />View All Rides</Link>
                    <Link to="/pending-order"><i className="ri-time-line" style={{ color: '#ef4444' }} />Pending Rides</Link>
                    <Link to="/reports/drivers"><i className="ri-line-chart-line" style={{ color: '#10b981' }} />Drivers Reports</Link>
                    <Link to="/reports/invoices"><i className="ri-file-list-3-line" style={{ color: '#6d5dfc' }} />Invoice Reports</Link>
                  </div>
                </section>
              </div>

                 <section className="dash-panel recent-panel mt-4">
                  <div className="panel-head">
                    <h2>Recent Rides</h2>
                    <Link to="/all-order">View All</Link>
                  </div>
                  <div className="recent-list">
                    {dashboard.recent.length === 0 ? <p className="empty-text">No recent rides</p> : dashboard.recent.map((order) => {
                      const cfg = statusConfig[Number(order.status)] || statusConfig[1];
                      const pickup = order?.items?.[0]?.pickup || order?.pickup || 'Pickup';
                      const drop = order?.items?.[0]?.drop || order?.drop || 'Drop';
                      return (
                        <Link className="recent-row" to={`/order/${order._id}`} key={order._id}>
                          <div className="ride-icon" style={{ color: cfg.color, background: cfg.bg }}><i className="ri-taxi-line"></i></div>
                          <div className="recent-id">
                            <strong>#{order.orderId || order._id?.slice(-6)}</strong>
                            <span>{order?.details?.[0]?.username || 'Customer'}</span>
                          </div>
                          <p title={`${pickup} -> ${drop}`}>{pickup} <i className="ri-arrow-right-line"></i> {drop}</p>
                          <em>{getDateValue(order)?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) || '--'}</em>
                          <small style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</small>
                        </Link>
                      );
                    })}
                  </div>
                </section>

            </>
          )}
        </div>

        <style>{`
          .modern-dashboard { padding: 22px; background: #f8fafc; min-height: calc(100vh - 70px); color: #111827; }
          .stats-grid { display: grid; grid-template-columns: repeat(5, minmax(170px, 1fr)); gap: 18px; margin-bottom: 20px; }
          .stat-card, .dash-panel { background: #fff; border: 1px solid #edf1f5; border-radius: 8px; box-shadow: 0 2px 12px rgba(15, 23, 42, .04); }
          .stat-card { padding: 20px 22px 12px; min-height: 154px; }
          .stat-top { display: flex; gap: 16px; align-items: flex-start; }
          .stat-icon { width: 48px; height: 48px; border-radius: 50%; display: grid; place-items: center; font-size: 25px; flex-shrink: 0; }
          .stat-top span { display: block; font-size: 13px; font-weight: 700; color: #64748b; margin-bottom: 6px; }
          .stat-top strong { display: block; font-size: 25px; line-height: 1; margin-bottom: 8px; }
          .stat-top small { font-size: 12px; font-weight: 700; white-space: nowrap; }
          .trend-up { color: #10b981; } .trend-down { color: #ef4444; }
          .sparkline { width: 100%; height: 42px; margin-top: 14px; }
          .main-grid { display: grid; grid-template-columns: 1.7fr 1fr 1fr; gap: 20px; margin-bottom: 20px; align-items: start; }
          .bottom-grid { display: grid; grid-template-columns: 1fr 1.65fr 1.25fr; gap: 20px; align-items: start; }
          .dash-panel { padding: 22px; min-width: 0; }
          .panel-head { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 18px; }
          .panel-head h2 { margin: 0; font-size: 17px; font-weight: 800; }
          .panel-head a, .panel-head span { border: 1px solid #edf1f5; border-radius: 7px; padding: 9px 15px; color: #475569; font-size: 12px; font-weight: 800; text-decoration: none; background: #fff; white-space: nowrap; }
          .chart-legend { display: flex; justify-content: center; gap: 28px; color: #64748b; font-size: 12px; font-weight: 700; margin-bottom: 10px; }
          .chart-legend span { display: inline-flex; align-items: center; gap: 8px; }
          .chart-legend i, .donut-row i { width: 9px; height: 9px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
          .line-chart { width: 100%; height: 230px; }
          .chart-days { display: grid; grid-template-columns: repeat(7, 1fr); color: #64748b; font-size: 12px; font-weight: 700; }
          .donut-layout { display: flex; align-items: center; justify-content: center; gap: 26px; min-height: 246px; flex-wrap: wrap; }
          .donut-box { position: relative; width: 210px; height: 210px; flex-shrink: 0; }
          .donut-chart { width: 210px; height: 210px; }
          .donut-center { position: absolute; inset: 0; display: grid; place-content: center; text-align: center; }
          .donut-center strong { font-size: 26px; }
          .donut-center span { color: #64748b; font-size: 12px; font-weight: 700; }
          .donut-list { display: grid; gap: 18px; min-width: 150px; }
          .donut-row { display: grid; grid-template-columns: 12px 1fr; gap: 8px 10px; align-items: center; color: #64748b; font-size: 12px; font-weight: 800; }
          .donut-row strong { grid-column: 2; color: #334155; font-weight: 700; white-space: nowrap; }

          /* ---- Fixed: consistent row alignment across Live Drivers / Top Drivers / Recent Rides ---- */
          .list-stack, .rank-list, .recent-list { display: grid; gap: 2px; }

          .driver-row, .rank-row, .recent-row {
            display: grid;
            align-items: center;
            gap: 10px;
            min-height: 52px;
            border-bottom: 1px solid #f1f5f9;
            text-decoration: none;
            color: #111827;
          }

          .driver-row { grid-template-columns: 32px minmax(0, 1fr) auto auto 16px; }
          .rank-row { grid-template-columns: 24px 32px minmax(0, 1fr) auto auto; }
          .recent-row { grid-template-columns: 42px 110px minmax(0, 1fr) 64px 96px; }

          /* Every text cell truncates instead of wrapping, so row height and column
             alignment stay fixed no matter how long the underlying data is. */
          .driver-row strong,
          .rank-row strong,
          .recent-id strong,
          .recent-id span,
          .recent-row p {
            display: block;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .driver-row strong, .rank-row strong { font-size: 13px; }
          .driver-row span { color: #10b981; font-size: 12px; font-weight: 800; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
          .driver-row span i { width: 7px; height: 7px; border-radius: 50%; background: #10b981; flex-shrink: 0; }
          .driver-row em, .rank-row em { font-style: normal; color: #64748b; font-size: 12px; font-weight: 700; white-space: nowrap; }
          .dash-avatar { border-radius: 50%; object-fit: cover; flex-shrink: 0; }
          .dash-avatar-fallback { display: grid; place-items: center; background: #e0ecff; color: #1d4ed8; font-weight: 800; flex-shrink: 0; }
          .rank-row span { color: #64748b; font-weight: 800; font-size: 12px; }
          .rank-row small { color: #10b981; font-size: 12px; font-weight: 800; white-space: nowrap; }

          .ride-icon { width: 38px; height: 38px; border-radius: 50%; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
          .recent-id { min-width: 0; }
          .recent-row p { margin: 0; color: #64748b; font-size: 12px; font-weight: 700; display: flex; align-items: center; gap: 4px; }
          .recent-row p i { flex-shrink: 0; font-size: 12px; }
          .recent-row em { margin: 0; color: #64748b; font-size: 12px; font-weight: 700; font-style: normal; white-space: nowrap; }
          .recent-row small { padding: 6px 10px; border-radius: 6px; font-size: 12px; font-weight: 800; text-align: center; white-space: nowrap; }
          /* ---- end fix ---- */

          .quick-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
          .quick-grid a { min-height: 76px; border: 1px solid #edf1f5; border-radius: 8px; display: grid; place-items: center; gap: 7px; color: #334155; text-decoration: none; font-size: 13px; font-weight: 800; background: #fff; }
          .quick-grid i { font-size: 25px; }
          .empty-text { margin: 18px 0; color: #94a3b8; font-weight: 700; text-align: center; }
          .simple-dash-card { display: block; padding: 24px; color: #fff; border-radius: 8px; font-weight: 800; text-decoration: none; margin-bottom: 16px; }
          @media (max-width: 1320px) {
            .stats-grid { grid-template-columns: repeat(3, 1fr); }
            .main-grid, .bottom-grid { grid-template-columns: 1fr; }
          }
          @media (max-width: 720px) {
            .modern-dashboard { padding: 14px; }
            .stats-grid { grid-template-columns: 1fr; }
            .donut-layout { flex-direction: column; }
            .recent-row { grid-template-columns: 42px minmax(0, 1fr) auto; }
            .recent-row .recent-id { grid-row: 1; }
            .recent-row p { grid-column: 2 / -1; }
            .recent-row em, .recent-row small { justify-self: start; }
            .quick-grid { grid-template-columns: 1fr; }
          }
        `}</style>
      </main>
    </>
  );
}

export default Dashboard;
